import React from "react";
import { Link } from "react-router-dom";
import { FacebookLogo, InstagramLogo, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { LOGO_URL } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";

export default function Footer() {
  const site = useSite();
  const c = useContent();

  return (
    <footer className="bg-atc-black text-white relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:4rem_4rem] opacity-50 pointer-events-none" />
      <div className="container-max relative py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO_URL} alt="" className="h-16 w-16 object-contain" />
            <div>
              <div className="font-display text-2xl leading-none">ATC HOME PRO'S</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mt-1">{site?.address || "Loganville, PA"}</div>
            </div>
          </div>
          <p className="text-white/60 text-sm max-w-sm">
            {pick(c, "global.footer_blurb", "Family-owned home improvement & construction. Lifetime labor warranty on every project.")}
          </p>
          <div className="flex gap-3 mt-5">
            {site?.facebook_url && (
              <a href={site.facebook_url} target="_blank" rel="noreferrer" className="border border-white/30 p-2 hover:border-atc-red hover:text-atc-red" data-testid="footer-facebook">
                <FacebookLogo size={18} weight="bold" />
              </a>
            )}
            {site?.instagram_url && (
              <a href={site.instagram_url} target="_blank" rel="noreferrer" className="border border-white/30 p-2 hover:border-atc-red hover:text-atc-red" data-testid="footer-instagram">
                <InstagramLogo size={18} weight="bold" />
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="overline mb-4">Explore</div>
          <ul className="space-y-2 font-heading uppercase text-sm tracking-wide">
            <li><Link to="/" className="hover:text-atc-red">Home</Link></li>
            <li><Link to="/services" className="hover:text-atc-red">Services</Link></li>
            <li><Link to="/gallery" className="hover:text-atc-red">Gallery</Link></li>
            <li><Link to="/blog" className="hover:text-atc-red">Journal</Link></li>
            <li><Link to="/contact" className="hover:text-atc-red">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-white/80">
            {site?.phone && (
              <li className="flex items-start gap-2">
                <Phone size={16} weight="bold" className="mt-0.5 text-atc-red" />
                <a href={`tel:${site.phone}`} className="hover:text-white" data-testid="footer-phone">{site.phone}</a>
              </li>
            )}
            {site?.email && (
              <li className="flex items-start gap-2">
                <EnvelopeSimple size={16} weight="bold" className="mt-0.5 text-atc-red" />
                <a href={`mailto:${site.email}`} className="hover:text-white" data-testid="footer-email">{site.email}</a>
              </li>
            )}
            {site?.address && (
              <li className="flex items-start gap-2">
                <MapPin size={16} weight="bold" className="mt-0.5 text-atc-red" />
                <span>{site.address}</span>
              </li>
            )}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Hours</div>
          <ul className="space-y-2 text-sm text-white/80 font-mono">
            <li>Mon–Fri · {site?.hours_mon_fri || "8:00 AM – 6:00 PM"}</li>
            <li>Sat · {site?.hours_sat || "By Appointment"}</li>
            <li>Sun · {site?.hours_sun || "Closed"}</li>
          </ul>
        </div>
      </div>
      <div className="relative border-t border-white/10">
        <div className="container-max py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/50 text-xs font-mono uppercase tracking-widest">
            © {new Date().getFullYear()} ATC Home Pro's · All Rights Reserved
          </p>
          <Link to="/admin" className="text-white/40 hover:text-white/80 text-xs font-mono uppercase tracking-widest" data-testid="admin-link">
            Admin Login
          </Link>
        </div>
      </div>
    </footer>
  );
}
